import { KeyIcon } from "@heroicons/react/outline";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { APIKeysSection } from "@/routes/settings/ProviderApiKeys";
import { ApiKeyStoreScope } from "@/routes/settings/ProviderApiKeys/store/api-key-store";

interface ProviderApiKeyPanelProps {
  provider: string;
  onAfterProviderKeySaved: () => void;
}

enum ProviderApiKeyProvider {
  OPENAI = "openai",
  ANTHROPIC = "anthropic",
  COHERE = "cohere",
  MISTRAL = "mistral",
  OPENROUTER = "openrouter",
  GOOGLE = "google",
  BEDROCK = "bedrock",
  AZURE = "azure",
  VERTEX_AI = "vertexai",
}

enum ProviderApiKeyDisplayName {
  OPENAI = "OpenAI",
  ANTHROPIC = "Anthropic",
  COHERE = "Cohere",
  MISTRAL = "Mistral",
  OPENROUTER = "OpenRouter",
  GOOGLE = "Google",
  BEDROCK = "Amazon Bedrock",
  AZURE = "Azure OpenAI",
  VERTEX_AI = "Vertex AI",
}

const PROVIDER_DISPLAY_NAMES: Record<ProviderApiKeyProvider, ProviderApiKeyDisplayName> = {
  [ProviderApiKeyProvider.OPENAI]: ProviderApiKeyDisplayName.OPENAI,
  [ProviderApiKeyProvider.ANTHROPIC]: ProviderApiKeyDisplayName.ANTHROPIC,
  [ProviderApiKeyProvider.COHERE]: ProviderApiKeyDisplayName.COHERE,
  [ProviderApiKeyProvider.MISTRAL]: ProviderApiKeyDisplayName.MISTRAL,
  [ProviderApiKeyProvider.OPENROUTER]: ProviderApiKeyDisplayName.OPENROUTER,
  [ProviderApiKeyProvider.GOOGLE]: ProviderApiKeyDisplayName.GOOGLE,
  [ProviderApiKeyProvider.BEDROCK]: ProviderApiKeyDisplayName.BEDROCK,
  [ProviderApiKeyProvider.AZURE]: ProviderApiKeyDisplayName.AZURE,
  [ProviderApiKeyProvider.VERTEX_AI]: ProviderApiKeyDisplayName.VERTEX_AI,
};

const getProviderDisplayName = (provider: string) =>
  PROVIDER_DISPLAY_NAMES[provider as ProviderApiKeyProvider] ?? "provider";

export const ProviderApiKeyPanel = ({
  provider,
  onAfterProviderKeySaved,
}: ProviderApiKeyPanelProps) => {
  const providerDisplayName = getProviderDisplayName(provider);

  return (
    <ApiKeyStoreScope>
      <Card
        data-provider-api-key-panel
        className="overflow-hidden border-amber-200/80 bg-white shadow-sm dark:border-amber-900/70 dark:bg-slate-950"
      >
        <CardHeader className="gap-3 border-b border-amber-100/80 bg-amber-50/70 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-200">
                <KeyIcon className="h-4 w-4" />
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base font-semibold tracking-normal text-slate-900 dark:text-slate-100">
                    Add your API key
                  </CardTitle>
                  <Badge variant={BadgeVariant.AMBER} size={BadgeSize.SM} text="Required" />
                </div>
                <CardDescription className="max-w-2xl text-sm leading-5 text-amber-950/80 dark:text-amber-100/80">
                  This request needs your {providerDisplayName} credentials. Save a key for this
                  workspace and the request will retry automatically.
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 p-4">
          <div className="rounded-lg border border-dashed border-amber-200/80 bg-white/80 p-4 dark:border-amber-900/70 dark:bg-slate-950/40">
            <APIKeysSection provider={provider} onAfterProviderKeySaved={onAfterProviderKeySaved} />
          </div>
          <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
            Your key is saved to this workspace&apos;s provider settings and only used for the
            selected provider.
          </p>
        </CardContent>
      </Card>
    </ApiKeyStoreScope>
  );
};
